<script setup lang="ts">
import type { Test, TestResult } from "@/components/TestHarness/useTestHarnessRunner"
import { KButton } from "@kaleidoscope-solutions/ks-design-system"
import { CloseIcon, DragHandleIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { useEventListener, useFileSystemAccess } from "@vueuse/core"
import { storeToRefs } from "pinia"
import { computed, nextTick, onMounted, ref, watch } from "vue"
import { onBeforeRouteLeave } from "vue-router"
import Draggable from "vuedraggable"
import RunIcon from "@/components/TestHarness/RunIcon.svg?component"
import TestCard from "@/components/TestHarness/TestCard.vue"
import { useTestHarnessRunner } from "@/components/TestHarness/useTestHarnessRunner"
import { useTestHarnessStore } from "@/stores/testHarness"
import { useSnackbar } from "@/utils/useSnackbar"

const { addSnackbar } = useSnackbar()

const { runTest, parseYamlTests, exportYamlTests } = useTestHarnessRunner()

const testHarnessStore = useTestHarnessStore()
const { tests } = storeToRefs(testHarnessStore)
const testResults = ref<TestResult[]>([])
function testsChecksum(val: unknown) {
  try {
    return JSON.stringify(val).length
  } catch {
    return 0
  }
}

const initialChecksum = ref<number | null>(null)

onMounted(() => {
  initialChecksum.value = testsChecksum(tests.value)
})

const isDirty = computed(() => {
  if (initialChecksum.value === null) {
    return false
  }
  return testsChecksum(tests.value) !== initialChecksum.value
})

useEventListener(window, "beforeunload", (e: BeforeUnloadEvent) => {
  if (!isDirty.value) {
    return
  }
  e.preventDefault()
})
onBeforeRouteLeave((_to, _from, next) => {
  if (!isDirty.value) {
    return next()
  }
  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(
    "You have unsaved changes to tests. Leaving may discard them. Are you sure you want to leave?"
  )

  if (confirmed) {
    // If confirmed, we can clear the localStorage and proceed with the navigation
    testHarnessStore.clearTests()
    return next()
  } else {
    return next(false)
  }
})

watch(
  tests,
  (val) => {
    if (val.length > testResults.value.length) {
      for (let i = testResults.value.length; i < val.length; i++) {
        testResults.value[i] = {}
      }
    } else if (val.length < testResults.value.length) {
      testResults.value.splice(val.length)
    }
  },
  { deep: true, immediate: true }
)

const deleteConfirm = ref<string | null>()

const {
  isSupported: fsSupported,
  data: fsData,
  file: fsFileHandle,
  fileName: fsFileName,
  open: fsOpen,
  save: fsSave,
  saveAs: fsSaveAs,
} = useFileSystemAccess({
  dataType: "Text",
  types: [
    {
      description: "YAML files",
      accept: {
        "text/yaml": [".yaml", ".yml"],
        "application/x-yaml": [".yaml", ".yml"],
      },
    },
  ],
})

async function loadFromFile() {
  try {
    await fsOpen()
    const content = fsData.value
    if (typeof content !== "string") {
      throw new TypeError("Loaded file is not text")
    }
    tests.value = []
    await nextTick()
    try {
      const loaded = parseYamlTests(content)
      testHarnessStore.setTests(loaded)
      initialChecksum.value = testsChecksum(tests.value)
      addSnackbar({
        type: "success",
        message: `Loaded ${tests.value.length} tests${fsFileName.value ? ` from ${fsFileName.value}` : ""}`,
      })
    } catch (e: any) {
      addSnackbar({ type: "error", message: `Invalid YAML: ${e?.message ?? e}` })
    }
  } catch (e: any) {
    if (e?.name !== "AbortError") {
      addSnackbar({ type: "error", message: `Failed to load file: ${e?.message ?? e}` })
    }
  }
}

async function saveToFile() {
  try {
    const yaml = exportYamlTests(tests.value)
    fsData.value = yaml
    if (fsFileHandle.value) {
      await fsSave()
    } else {
      const now = new Date()
      const dateTime = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}`
      await fsSaveAs({ suggestedName: `test-${dateTime}` })
    }
    initialChecksum.value = testsChecksum(tests.value)
    addSnackbar({ type: "success", message: fsFileName.value ? `Saved to ${fsFileName.value}` : "Saved" })
  } catch (e: any) {
    if (e?.name !== "AbortError") {
      addSnackbar({ type: "error", message: `Failed to save file: ${e?.message ?? e}` })
    }
  }
}

function clearFileBinding() {
  fsFileHandle.value = undefined
  fsData.value = ""
  addSnackbar({ type: "success", message: "File binding cleared" })
}

const summary = computed(() => {
  let passed = 0
  let failed = 0
  let error = 0
  for (const display of testResults.value) {
    if (display.error) {
      error++
    } else if (display.pass === true) {
      passed++
    } else if (display.pass === false) {
      failed++
    }
  }
  return { passed, failed, error }
})

const activeFilters = ref<string[]>([])
const searchQuery = ref("")
const dragDisabled = computed(() => (searchQuery.value.trim() !== "") || activeFilters.value.length > 0)

watch(
  searchQuery,
  (newVal, oldVal) => {
    if ((oldVal ?? "").trim() === "" && newVal.trim() !== "") {
      activeFilters.value = []
    }
  }
)

function toggleFilter(status: string) {
  const index = activeFilters.value.indexOf(status)
  if (index === -1) {
    activeFilters.value.push(status)
  } else {
    activeFilters.value.splice(index, 1)
  }
}

const filteredTests = computed(() => {
  const rows = tests.value.map((test, i) => ({ test, display: testResults.value[i], index: i }))

  const q = searchQuery.value.trim().toLowerCase()
  const bySearch = q
    ? rows.filter(({ test, display }) => {
        const haystackParts: Array<string | undefined> = [
          test.name,
          test.note,
          test.formula,
          test.contextId,
          test.expected,
          display?.actual,
          display?.error, // why not
        ]
        const haystack = haystackParts
          .filter((v): v is string => typeof v === "string" && v.length > 0)
          .join("\n")
          .toLowerCase()
        return haystack.includes(q)
      })
    : rows

  // Status filters are applied AFTER the search (if any are selected)
  if (activeFilters.value.length === 0) {
    return bySearch
  }

  return bySearch.filter(({ display }) => {
    if (activeFilters.value.includes("error") && display?.error) {
      return true
    }
    if (activeFilters.value.includes("passed") && display?.pass === true && !display?.error) {
      return true
    }
    if (activeFilters.value.includes("failed") && display?.pass === false && !display?.error) {
      return true
    }
    return false
  })
})

const running = computed(() => testResults.value.some((display: TestResult) => display.running))

async function runByIndex(indexes: number[]) {
  await Promise.all(indexes.map(async (idx) => {
    const test = tests.value[idx]
    if (!test) {
      return
    }

    testResults.value[idx].running = true

    try {
      const updated = await runTest({ ...test })
      testResults.value[idx] = updated
    } catch (error) {
      console.error("Error running test:", error)
      testResults.value[idx] = { error: String(error) }
    } finally {
      testResults.value[idx].running = false
    }
  }))
}

function runAll() {
  runByIndex(tests.value.map((_, i) => i))
}

function addTest() {
  testHarnessStore.addTest({
    id: crypto.randomUUID(),
    name: "",
    formula: "",
    expected: ""
  })
  testResults.value.unshift({})
}

function getTestIndexFromId(id: string) {
  return tests.value.findIndex((test) => test.id === id)
}

function deleteTest(test: Test) {
  if (deleteConfirm.value !== test.id) {
    deleteConfirm.value = test.id
    setTimeout(() => {
      deleteConfirm.value = null
    }, 3000)
    return
  }

  const index = getTestIndexFromId(test.id)
  testHarnessStore.deleteTest(test.id)
  testResults.value.splice(index, 1)
  deleteConfirm.value = null
}

function clearSearch() {
  searchQuery.value = ""
  activeFilters.value = []
}

function onReorderChange(evt: any) {
  if (evt?.moved) {
    const { oldIndex, newIndex } = evt.moved
    if (typeof oldIndex === "number" && typeof newIndex === "number") {
      const moved = testResults.value.splice(oldIndex, 1)[0]
      testResults.value.splice(newIndex, 0, moved)
    }
  }
  testHarnessStore.setTests([...(tests.value ?? [])])
  deleteConfirm.value = null
}
</script>

<template>
  <div class="ph-no-capture flex size-full flex-col overflow-hidden rounded-lg bg-gray-50 shadow-sm">
    <div class="sticky top-0 z-10 flex flex-col gap-4 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-center gap-3">
        <h2 class="text-2xl font-bold tracking-tight text-gray-900">
          Test Harness
        </h2>
        <span
          v-if="tests.length > 0"
          class="ml-2 text-xs font-medium text-gray-500"
        >({{ tests.length }} loaded)</span>
      </div>
      <div class="flex items-center gap-2">
        <span
          v-if="fsFileName"
          class="flex items-center gap-1 rounded-sm bg-gray-100 px-2 py-0.5"
        >
          <CloseIcon
            class="size-4 cursor-pointer"
            title="Clear binding"
            @click="clearFileBinding"
          />
          <span

            class="font-medium text-gray-700"
            :title="fsFileName"
            aria-live="polite"
          >
            {{ fsFileName }}
          </span>
        </span>
        <KButton
          color="violet"
          class="shadow-none"
          :disabled="!fsSupported"
          :title="!fsSupported ? 'File System Access API not supported in this browser' : ''"
          @click="loadFromFile"
        >
          Load
        </KButton>
        <KButton
          v-if="tests.length > 0"
          variety="secondary"
          color="violet"
          class="shadow-none"
          :disabled="!fsSupported"
          :title="!fsSupported ? 'File System Access API not supported in this browser' : ''"
          @click="saveToFile"
        >
          {{ fsFileHandle ? 'Save' : 'Save As' }}
        </KButton>
      </div>
    </div>

    <div
      v-if="tests.length > 0"
      class="flex flex-col items-start gap-4 bg-gray-50 px-6 py-3"
    >
      <KButton
        variety="primary"
        color="violet"
        class="min-w-[140px] shadow-none"
        :disabled="running"
        @click="runAll()"
      >
        <span class="flex items-center gap-2">
          <span>{{ running ? 'Running...' : 'Run All Tests' }}</span>
          <RunIcon class="size-5" />
        </span>
      </KButton>
      <div class="flex w-full flex-col gap-3 md:justify-between">
        <div class="flex-1">
          <label
            class="flex flex-col font-medium text-gray-700"
          >
            Search
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search tests (name, note, formula, expected, result...)"
              class="allow-undo w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-700 shadow-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-200 focus:outline-none md:max-w-md"
            >
          </label>
        </div>
        <div class="flex gap-2">
          <button
            class="flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-3 py-1 font-semibold text-green-700 transition-all select-none hover:bg-green-100 focus:outline-none"
            :class="activeFilters.includes('passed') ? 'bg-green-100 ring-2 ring-green-400' : ''"
            @click="toggleFilter('passed')"
          >
            <span>✔️ Passed</span>
            <span class="ml-1 font-bold">{{ summary.passed }}</span>
          </button>
          <button
            class="flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1 font-semibold text-red-700 transition-all select-none hover:bg-red-100 focus:outline-none"
            :class="activeFilters.includes('failed') ? 'bg-red-100 ring-2 ring-red-400' : ''"
            @click="toggleFilter('failed')"
          >
            <span>❌ Failed</span>
            <span class="ml-1 font-bold">{{ summary.failed }}</span>
          </button>
          <button
            class="flex items-center gap-1 rounded-full border border-yellow-200 bg-yellow-50 px-3 py-1 font-semibold text-yellow-700 transition-all select-none hover:bg-yellow-100 focus:outline-none"
            :class="activeFilters.includes('error') ? 'bg-yellow-100 ring-2 ring-yellow-400' : ''"
            @click="toggleFilter('error')"
          >
            <span>⚠️ Error</span>
            <span class="ml-1 font-bold">{{ summary.error }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto bg-gray-50 px-6 py-4">
      <div
        v-if="filteredTests.length === 0"
        class="flex h-80 flex-col items-center justify-center text-gray-600"
      >
        <span class="mb-2 text-4xl">🧪</span>
        <span class="text-lg font-medium">No tests to display</span>
        <span
          v-if="tests.length > 0"
          class="mt-2 flex flex-col items-center gap-2 text-center text-sm"
        >
          Try changing your search.
          <KButton @click="clearSearch">Clear Search</KButton>
        </span>
        <span
          v-else
          class="text-sm"
        >
          Try loading or adding a test.
        </span>
      </div>
      <div v-else>
        <div
          v-if="dragDisabled"
          class="grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <TestCard
            v-for="({ test, display, index: i }) in filteredTests"
            :key="test.id"
            :test="test"
            :display="display"
            :delete-confirm="deleteConfirm ?? undefined"
            @run="runByIndex([i])"
            @delete="deleteTest(test)"
          />
        </div>
        <Draggable
          v-else
          v-model="tests"
          tag="div"
          :animation="200"
          item-key="id"
          handle="[data-handle]"
          ghost-class="opacity-50"
          class="grid grid-cols-1 gap-4 md:grid-cols-2"
          @change="onReorderChange"
        >
          <template #item="{ element: test, index: i }">
            <div class="relative">
              <div
                class="absolute top-0 left-0 z-10 cursor-grab rounded-sm p-1 text-gray-700 hover:text-gray-600"
                data-handle
                title="Drag to reorder"
              >
                <DragHandleIcon class="size-3" />
              </div>
              <TestCard
                :key="test.id"
                :test="test"
                :display="testResults[i]"
                :delete-confirm="deleteConfirm ?? undefined"
                @run="runByIndex([i])"
                @delete="deleteTest(test)"
              />
            </div>
          </template>
        </Draggable>
      </div>
    </div>
  </div>
  <div class="fixed right-8 bottom-8 z-30">
    <KButton
      color="teal"
      variety="primary"
      class="flex size-14 items-center justify-center rounded-full text-3xl shadow-lg transition-transform duration-150 hover:scale-105"
      title="Add new test"
      aria-label="Add new test"
      @click="addTest"
    >
      <span class="sr-only">Add new test</span>
      <svg
        class="size-8"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 4v16m8-8H4"
        />
      </svg>
    </KButton>
  </div>
</template>
